import { CanonicalBusinessEvent } from "../types/cbes";
import { DigitalBusinessDocument } from "../types/documents";
import { AccountingEventsGroup, BundleComponent } from "../types/events";

export const getAllProductIds = (
  documents: DigitalBusinessDocument[]
): Set<string> => {
  const allProductIds = new Set<string>();
  documents.forEach((doc) => {
    if (doc?.body?.body?.products) {
      Object.keys(doc.body.body.products).forEach((productId) => {
        allProductIds.add(productId);
      });
    }
  });
  return allProductIds;
};

const parseBundleComponents = (
  bundleComponents?: BundleComponent[] | string
): BundleComponent[] => {
  if (!bundleComponents) {
    return [];
  }

  if (Array.isArray(bundleComponents)) {
    return bundleComponents;
  }

  try {
    const parsed: unknown = JSON.parse(bundleComponents);
    return Array.isArray(parsed) ? (parsed as BundleComponent[]) : [];
  } catch {
    return [];
  }
};

export const getAccountingBundleProductIds = (
  accountingEventGroups: AccountingEventsGroup[]
): Set<string> => {
  const allProductIds = new Set<string>();

  accountingEventGroups.forEach((group) => {
    Object.values(group.accountingEvents || {}).forEach((event) => {
      parseBundleComponents(event.bundleComponents).forEach((component) => {
        const productId = component.offerId || component.pvid || component.gti;
        if (productId) {
          allProductIds.add(productId);
        }
      });
    });
  });

  return allProductIds;
};

export const isDocumentBundleTransaction = (
  document?: DigitalBusinessDocument
): boolean => {
  const attrs = document?.body?.metadata?.internalAttributes;
  return (
    attrs?.IS_BUNDLE_IMMEDIATE === "true" ||
    attrs?.IS_SEASON_PASS_BUNDLE === "true" ||
    attrs?.IS_PREORDER_BUNDLE === "true"
  );
};

export const isBundleNonSplitTransaction = (
  documents: DigitalBusinessDocument[]
): boolean => {
  if (documents.length === 0) return false;

  const allProductIds = getAllProductIds(documents);

  return isDocumentBundleTransaction(documents[0]) && allProductIds.size === 1;
};

export const groupCbesByProductIds = (
  cbes: CanonicalBusinessEvent[],
  productIds: string[]
): Map<string, CanonicalBusinessEvent[]> => {
  const groups = new Map<string, CanonicalBusinessEvent[]>();

  cbes.forEach((cbe) => {
    const matchedProduct = productIds.find((productId) =>
      cbe.idempotenceId.includes(productId)
    );

    if (!matchedProduct) return;

    if (!groups.has(matchedProduct)) {
      groups.set(matchedProduct, []);
    }
    groups.get(matchedProduct)?.push(cbe);
  });

  groups.forEach((cbeList) => {
    cbeList.sort((a, b) => a.lockVersion - b.lockVersion);
  });

  return groups;
};
